/** The link in the email, landing back on the app.
 *
 *  Confirmation and password-reset mails send the reader to `authRedirectUrl()`
 *  carrying a credential. This module is what happens next: it turns that
 *  landing into ONE fact — `AuthLanding` — and hands it to whoever is listening.
 *  Nothing here draws anything; the screen lives in `ui/authCallback.ts`, which
 *  is fetched only if something actually landed.
 *
 *  ── Why Forgot Password needed this at all ─────────────────────────────────
 *  Reset mail is sent SERVER-side, by the `bea-account` Edge Function, so no
 *  PKCE code verifier is ever created in this browser. GoTrue therefore issues
 *  an implicit recovery link and it comes back in the url hash:
 *
 *      https://app/#access_token=…&refresh_token=…&type=recovery
 *
 *  supabase-js refuses that under `flowType: 'pkce'` (`Not a valid PKCE flow
 *  url.`), so no session was established, `PASSWORD_RECOVERY` never fired, and —
 *  since nothing in the app ever asked for a new password — Forgot Password did
 *  precisely nothing: the reader landed signed out, with live tokens sitting in
 *  the address bar and not one word on screen. `captureAuthCallback()` in
 *  `supabaseClient.ts` now takes that hash out of the url before either
 *  supabase-js or the router can trip over it, and this module adopts it.
 *
 *  Both link shapes end in the same place:
 *    • implicit `#access_token=…&type=recovery` — the Edge Function's mail;
 *    • PKCE `?code=…` — what the client-side `resetPasswordForEmail` fallback
 *      sends, consumed by supabase-js, which then fires `PASSWORD_RECOVERY`.
 */
import { activeAuth, type OAuthProvider } from './authAdapter';
import { authCallbackPending, pendingAuthCallback, pkceCodeAtLoad, supabase } from './supabaseClient';
import { failureFromCode, providerFailureMessage, takeProviderLeave, type ProviderFailure } from './providerReturn';

/** What the link this page was opened by turned out to be. */
export type AuthLanding =
  /** A password reset is under way and a new password must be chosen NOW,
   *  while the recovery session is live. */
  | { kind: 'recovery'; email: string }
  /** A confirmation / magic link signed the reader in. Nothing to ask them. */
  | { kind: 'signed-in'; email: string }
  /** The link was expired, already used, or refused. Say so plainly. */
  | { kind: 'link-failed'; message: string }
  /** Back from Google or GitHub, signed in. */
  | { kind: 'provider-signed-in'; provider: OAuthProvider; email: string }
  /** Back from Google or GitHub (or a code nobody here asked for), NOT signed
   *  in. `provider` is null when this browser never sent the reader anywhere. */
  | { kind: 'provider-failed'; provider: OAuthProvider | null; failure: ProviderFailure; message: string };

type Listener = (landing: AuthLanding) => void;

const listeners = new Set<Listener>();
let delivered: AuthLanding | null = null;
/** A reset can be announced by both routes (an implicit hash we adopt, and the
 *  `PASSWORD_RECOVERY` supabase-js fires for the PKCE one). The reader must be
 *  asked once. */
let recoveryAnnounced = false;

function announce(landing: AuthLanding): void {
  if (landing.kind === 'recovery') {
    if (recoveryAnnounced) return;
    recoveryAnnounced = true;
  }
  delivered = landing;
  for (const fn of listeners) fn(landing);
}

/** Listen for the landing. Fires immediately if one already happened, so a late
 *  subscriber (a lazily imported screen) cannot miss it. */
export function onAuthLanding(fn: Listener): () => void {
  listeners.add(fn);
  if (delivered) fn(delivered);
  return () => listeners.delete(fn);
}

/** Our own wording for a link that did not work.
 *
 *  GoTrue's own `error_description` is deliberately not shown: the house rule
 *  for this layer is that a provider message never reaches the reader verbatim.
 *  What they need is which of the two things happened and what to do. */
function linkFailureMessage(code: string): string {
  if (code === 'otp_expired' || code.includes('expired')) {
    return 'That link has expired. Reset and confirmation links only last a short while, so please request a new one.';
  }
  if (code === 'access_denied') {
    return 'That link has already been used, or it was cancelled. Please request a new one.';
  }
  return 'That link could not be used. Please request a new one.';
}

/** Adopt whatever the reader arrived with, and stay listening for the PKCE
 *  route. Safe to call on every boot: with no callback in the url and no
 *  recovery event, it does nothing at all.
 *
 *  Deliberately never throws — a boot step that can take the app down with it
 *  is a worse bug than the one it is fixing. */
export async function initAuthCallback(): Promise<void> {
  if (activeAuth.kind !== 'remote') return;
  // Nothing landed, so there is nothing to adopt and nothing to listen for —
  // and, since the accounts library is fetched on demand now, nothing to
  // DOWNLOAD either. An ordinary boot leaves this function immediately, which
  // is the whole point: `authCallbackPending()` answers from the url alone.
  if (!authCallbackPending()) return;
  // Which provider this page left for, if any (read once, cleared).
  const provider = takeProviderLeave();
  const providerFailed = (failure: ProviderFailure): void =>
    announce({ kind: 'provider-failed', provider, failure, message: providerFailureMessage(failure, provider) });

  // The PKCE route: supabase-js consumed a `?code=` whose stored verifier was
  // tagged as password recovery, and tells us through its own event. The
  // listener goes on as soon as the client exists, exactly as before.
  const client = await supabase().catch(() => null);
  try {
    client?.auth.onAuthStateChange((event, session) => {
      if (event !== 'PASSWORD_RECOVERY') return;
      announce({ kind: 'recovery', email: session?.user?.email ?? '' });
    });
  } catch { /* no client on this build; the guard above normally catches it */ }

  const cb = pendingAuthCallback();
  if (!cb) {
    await settlePkceCode(client, provider, providerFailed);
    return;
  }

  if (cb.kind === 'error') {
    // Back from a provider this page sent the reader to: it is not a "link".
    if (provider) { providerFailed(failureFromCode(cb.code)); return; }
    announce({ kind: 'link-failed', message: linkFailureMessage(cb.code.toLowerCase()) });
    return;
  }

  // An implicit callback. The tokens are already out of the url; hand them to
  // the client so the session it owns from here on is indistinguishable from
  // one opened by an ordinary sign-in — same storage, same refresh, same
  // onAuthStateChange for every subscriber in the app.
  const sb = client;
  if (!sb) {
    announce({ kind: 'link-failed', message: linkFailureMessage('') });
    return;
  }
  const { data, error } = await sb.auth.setSession({
    access_token: cb.accessToken,
    refresh_token: cb.refreshToken,
  }).catch((e: unknown) => ({ data: { user: null }, error: e as Error }));
  if (error || !data.user) {
    announce({ kind: 'link-failed', message: linkFailureMessage('') });
    return;
  }
  const email = data.user.email ?? '';
  announce(cb.type === 'recovery' ? { kind: 'recovery', email } : { kind: 'signed-in', email });
}

/** A PKCE `?code=` landing: supabase-js redeems it while the client starts up
 *  and takes it out of the url when that works. Afterwards the url answers the
 *  question: a code still there is a code that was NOT redeemed, which used to
 *  leave the reader signed out, the code in the address bar, and nothing said.
 *  A code with no verifier on this browser is one another browser started
 *  (supabase-js does not even try it), and one with a verifier was refused
 *  (expired, or already used by a reload of this very page). */
async function settlePkceCode(
  client: Awaited<ReturnType<typeof supabase>> | null,
  provider: OAuthProvider | null,
  providerFailed: (failure: ProviderFailure) => void,
): Promise<void> {
  const landed = pkceCodeAtLoad();
  if (!landed.code) return;
  if (!client) { providerFailed('failed'); return; }
  // Resolves once supabase-js has finished initialising, i.e. after its own
  // attempt at the code.
  const { data } = await client.auth.getSession().catch(() => ({ data: { session: null } }));
  const url = new URL(location.href);
  if (!url.searchParams.has('code')) {
    const email = data.session?.user?.email ?? '';
    if (provider && data.session) announce({ kind: 'provider-signed-in', provider, email });
    return;
  }
  url.searchParams.delete('code');
  url.searchParams.delete('sb_flow_id');
  try { history.replaceState(history.state, '', url.toString()); } catch { /* the message still stands */ }
  if (data.session) return; // an older session is still here; nothing was lost
  providerFailed(landed.verifier ? 'refused' : 'elsewhere');
}
